import { useEffect, useState } from "react";
import { Calendar } from "lucide-react";
import { EXHIBITION } from "../lib/exhibitionData";

function computeRemaining(target) {
  const now = new Date().getTime();
  const diff = target.getTime() - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, over: true };
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diff / (1000 * 60)) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s, over: false };
}

function Cell({ label, value, testid, isLast }) {
  return (
    <div
      className="flex flex-col items-start relative"
      data-testid={testid}
    >
      <span className="countdown-number text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] text-[#F5F0E8]">
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-3 text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-[#F5F0E8]/70">
        {label}
      </span>
      {!isLast && (
        <span
          aria-hidden="true"
          className="hidden sm:block absolute top-2 -right-3 md:-right-5 lg:-right-7 w-px bg-[#F5F0E8]/15"
          style={{ height: "62%" }}
        />
      )}
    </div>
  );
}

export default function Hero() {
  const [t, setT] = useState(() => computeRemaining(EXHIBITION.openingDate));

  useEffect(() => {
    const id = setInterval(
      () => setT(computeRemaining(EXHIBITION.openingDate)),
      1000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative h-screen min-h-[720px] w-full overflow-hidden noise-overlay"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/2271c5e8-a37c-4408-9c95-25716ec5ba08/images/d575271600c2c71edcc82216d3ba872417358f87711f4b5fee45904ddc2f4766.png"
          alt="Black and white documentary photograph of civic life in Dakar"
          className="w-full h-full object-cover"
          style={{ filter: "grayscale(100%) contrast(1.08)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/80" />
        {/* Subtle top vignette — anchors the institutional ribbon */}
        <div
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{
            height: "260px",
            background:
              "linear-gradient(to bottom, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.18) 60%, rgba(10,10,10,0) 100%)",
          }}
        />
      </div>

      {/* Top meta row: Arc presenter mark + exhibition credit.
          Aligned to the navigation grid (same px-* utilities) and stacks
          below the lg breakpoint so the two brand blocks never collide. */}
      <div
        className="relative z-10 px-6 md:px-12 lg:px-16"
        style={{ paddingTop: "clamp(120px, 14vw, 152px)" }}
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-9 max-w-[1500px]">
          {/* Arc Institute presenter mark */}
          <a
            href="#contact"
            aria-label="Presented by The Arc Institute"
            data-testid="hero-arc-logo"
            className="inline-flex items-center gap-4 shrink-0 group"
          >
            <img
              src="/brand/arc-logo.png"
              alt="The Arc Institute"
              className="h-11 w-11 md:h-12 md:w-12 lg:h-[52px] lg:w-[52px] object-contain pl-no-bw select-none shrink-0"
              style={{ filter: "brightness(0) invert(1)" }}
              draggable="false"
            />
            <span className="flex flex-col leading-snug">
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-[#F5F0E8]/65">
                Presented by
              </span>
              <span className="text-[12px] md:text-[13px] lg:text-[14px] uppercase tracking-[0.28em] text-[#F5F0E8] font-semibold whitespace-nowrap">
                The Arc Institute
              </span>
            </span>
          </a>

          {/* Divider — only on lg+ when both blocks share a row */}
          <span
            className="hidden lg:block bg-[#F5F0E8]/25 shrink-0"
            style={{ width: "1px", height: "40px" }}
            aria-hidden="true"
          />

          {/* Exhibition credit */}
          <span className="flex items-center gap-4 min-w-0">
            <span
              className="block bg-[#C41E1E] shrink-0"
              style={{ width: "36px", height: "2px" }}
            />
            <span className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] md:tracking-[0.34em] text-[#F5F0E8]/92 leading-snug font-medium">
              A Photographic Exhibition by Abdou Karim Ndoye
            </span>
          </span>
        </div>
      </div>

      {/* Opening-night banner — pill-style, centered, readable */}
      <div
        className="relative z-10 mt-6 md:mt-8 px-6 md:px-12 lg:px-16"
        style={{ textAlign: "center" }}
      >
        <div
          data-testid="hero-opening-banner"
          className="items-center bg-[#C41E1E] text-white"
          style={{
            padding: "14px 28px",
            fontSize: "14px",
            fontWeight: 700,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            gap: "14px",
            display: "inline-flex",
          }}
        >
          <span
            className="bg-white pl-quiet-pulse"
            style={{
              width: "5px",
              height: "5px",
              borderRadius: "50%",
              display: "block",
            }}
            aria-hidden="true"
          />
          <Calendar size={14} strokeWidth={2.5} aria-hidden="true" />
          <span>Opening Night · April 2, 2027</span>
        </div>
      </div>

      {/* Title block (bottom-left) */}
      <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 lg:px-16 pb-10 md:pb-14 lg:pb-16 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          <div className="lg:col-span-7 pl-rise">
            <h1
              className="font-serif-display text-[#F5F0E8] leading-[0.88] tracking-tight text-5xl sm:text-7xl md:text-8xl lg:text-[9rem]"
              style={{ marginBottom: "20px" }}
            >
              Première
              <br />
              <span className="italic font-normal">Ligne</span>
              <span className="text-[#C41E1E]">.</span>
            </h1>
            <p
              className="max-w-xl text-[#F5F0E8]/85 text-base md:text-lg leading-relaxed"
              style={{ marginTop: "16px" }}
            >
              A photographer's witness to the spring Senegal chose itself.
              Eleven months on the streets of Dakar, March 2024.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href="#"
                data-testid="hero-cta-tickets"
                className="pl-cta pl-cta-primary"
              >
                Get Tickets
                <span aria-hidden>→</span>
              </a>
              <a
                href="#"
                data-testid="hero-cta-donate"
                className="pl-cta pl-cta-ghost"
              >
                Donate
              </a>
            </div>
          </div>

          {/* Countdown */}
          <div
            className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[#F5F0E8]/20 pl-rise"
            data-testid="hero-countdown"
          >
            <div className="mb-3 flex items-center gap-3">
              <span className="w-2 h-2 bg-[#C41E1E] pl-quiet-pulse" />
              <span className="text-[10px] uppercase tracking-[0.35em] text-[#F5F0E8]/75">
                Countdown to doors
              </span>
            </div>
            <div
              data-testid="hero-countdown-grid"
              className="grid grid-cols-4 gap-x-6 sm:gap-x-10 md:gap-x-12 lg:gap-x-16"
            >
              <Cell label="Days" value={t.d} testid="countdown-days" />
              <Cell label="Hours" value={t.h} testid="countdown-hours" />
              <Cell label="Minutes" value={t.m} testid="countdown-minutes" />
              <Cell
                label="Seconds"
                value={t.s}
                testid="countdown-seconds"
                isLast
              />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 opacity-50">
        <span className="text-[9px] uppercase tracking-[0.4em] text-[#F5F0E8]">
          Scroll
        </span>
        <span className="block w-[1px] h-8 bg-[#F5F0E8]/40" />
      </div>
    </section>
  );
}
